import React, { useRef } from 'react';
import { styles } from './ListingBlock.css';
import DomFont from '@jbhi-fi/lanyard-ui/font/DomFont';
import { GoToLink, GoToLinkProps } from '../GoToLink/GoToLink';
import { ListingBlockHeading } from './ListingBlockHeading';
import { PossiblyResponsiveValue, useResponsiveValue } from '../hooks/useResponsiveValue';
import { spacing } from '@jbhi-fi/lanyard-ui/themes';
import { ContentThemePalette } from '../Contentful/transformers/ThemeTransformer';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { TrackingProvider } from '../Tracking';

export type ListingBlockProps = {
  children: React.ReactNode;
  heading?: string | React.ReactNode;
  headingAs?: Parameters<typeof DomFont>[0]['as'];
  subHeading?: string | React.ReactNode;
  linkProps?: Omit<GoToLinkProps, 'className'>;
  gap?: PossiblyResponsiveValue<React.CSSProperties['gap']>;
  palette?: ContentThemePalette;
  testId?: string;
};

export const ListingBlock = ({
  children,
  heading,
  linkProps,
  headingAs = 'h3',
  subHeading,
  gap: gapProp = { xs: spacing(5), md: spacing(7.5) },
  palette: paletteProp,
  testId,
}: ListingBlockProps) => {
  const headingContainerRef = useRef<HTMLSpanElement>(null);
  const gap = useResponsiveValue(gapProp);
  const {
    text: textColor = 'inherit',
    heading: headingColor = 'inherit',
    background: backgroundColor = 'transparent',
  } = paletteProp ?? {};

  return (
    <TrackingProvider shouldInheritFromParentContexts group={headingContainerRef.current?.innerText}>
      <div className={styles.listingBlockContainer} style={{ color: textColor, backgroundColor }} data-testid={testId}>
        {heading ? (
          <span ref={headingContainerRef} style={{ display: 'contents' }}>
            <ListingBlockHeading
              headingClassName={styles.heading}
              subHeadingClassName={styles.subHeading}
              heading={heading}
              headingAs={headingAs}
              subHeading={subHeading}
              headingColor={headingColor}
            />
          </span>
        ) : null}
        {linkProps?.href ? <GoToLink {...linkProps} className={styles.link} /> : null}
        <div className={styles.contents} style={{ margin: `${heading ? gap : 0} 0 0 0` }}>
          {children}
        </div>
      </div>
    </TrackingProvider>
  );
};
